import { getLocalizedDayNames } from '@/app/booking-form-utils'
import Button from '@/app/components/common/Button'
import FullScreenDrawer from '@/app/components/common/FullScreenDrawer'
import Popup from '@/app/components/common/Popup'
import FormInput from '@/app/components/Input'
import { Icons } from '@/app/components/ui/Icons'
import {
	AvailableInputTypes,
	defaultCheckInDate,
	defaultCheckOutDate,
	INPUT,
} from '@/app/constants'
import { getSearchCalendarMaxDate, isLowercaseMonth, scrollToSelectedMonth } from '@/app/utils'
import classNames from 'classnames'
import {
	eachDayOfInterval,
	format,
	isAfter,
	isFirstDayOfMonth,
	isLastDayOfMonth,
	isToday,
	startOfDay,
	subMonths,
} from 'date-fns'
import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import React, { useEffect, useRef, useState } from 'react'
import Datepicker from 'react-datepicker'
import { RemoveScroll } from 'react-remove-scroll'
import { emitDateClickGAEvent } from '../ga-event-helpers'

interface BookingDatepickerProps {
	dateFrom: Date | null
	dateTo: Date | null
	isDatepickerOpen: boolean
	openedInput: AvailableInputTypes
	isMobile: boolean
	isDesktop: boolean
	locale: Locale
	onDateChange: (dates: Date[]) => void
	onCalendarClose: () => void
	openCalendar: (input: INPUT) => void
	handleCancelDateSelection: () => void
	handleApplyDates: () => void
}
const DATE_FORMAT = 'dd MMM. yyyy'
const SHORT_DATE_FORMAT = 'dd MMM.'
const DEFAULT_MONTHS_TO_SHOW = 2
const MOBILE_MONTHS_TO_SHOW = 13
type HighlighteDays = [{ [key: string]: Date[] }]

const BookingDatepicker: React.FC<BookingDatepickerProps> = ({
	dateFrom,
	dateTo,
	isDatepickerOpen,
	openedInput,
	isMobile,
	isDesktop,
	locale,
	onDateChange,
	onCalendarClose,
	openCalendar,
	handleCancelDateSelection,
	handleApplyDates,
}) => {
	const [highlightedDays, setHighlightedDays] = useState<HighlighteDays | []>(
		[]
	)
	const isAr = locale.code === 'ar'
	const datepickerRef = useRef<Datepicker>(null)
	const calendarRef = useRef<HTMLDivElement>(null)
	const datepickerInputsRef = useRef<HTMLDivElement>(null)
	const bookingInputDateFromRef = useRef<HTMLInputElement>(null)
	const t = useTranslations('Form')
	const urlParams = useParams()
	const today = new Date()
	const selectedDate = dateFrom || new Date()
	const monthsDiff =
		(selectedDate.getFullYear() - today.getFullYear()) * 12 +
		selectedDate.getMonth() -
		today.getMonth()
	const initialVisibleMonth =
		dateFrom && dateFrom > today ? subMonths(dateFrom, monthsDiff) : today

	useEffect(() => {
		if (isMobile && isDatepickerOpen) {
			setTimeout(() => scrollToSelectedMonth(dateFrom as Date), 0)
		}
	}, [isDatepickerOpen, isMobile])

	const onDayHover = (date: Date) => {
		if (dateFrom && isAfter(date, dateFrom) && !dateTo) {
			setHighlightedDays([
				{
					'react-datepicker__day-highlight': eachDayOfInterval({
						start: dateFrom,
						end: date,
					}),
				},
			])
		}
	}

	const capitalize = !isLowercaseMonth(urlParams.lang as string)
	const onDayLeave = () => {
		setHighlightedDays([])
	}

	const renderDayContents = (day: number, date: Date) => {
		const isDateToday = isToday(startOfDay(date))

		return (
			<div
				onMouseOver={() => onDayHover(date)}
				onMouseLeave={onDayLeave}
				className={classNames('text-sm react-datepicker__day-btn', {
					'react-datepicker__day-btn--today': isDateToday,
				})}
			>
				{day}
			</div>
		)
	}

	const renderCalendarInputs = () => (
		<>
			<div ref={calendarRef} className="relative -top-7"></div>
			<div className="max-lg:hidden h-[30px] w-[1px] bg-text-300 mx-1"></div>

			<div
				ref={datepickerInputsRef}
				data-test-id="date-picker-input"
				className={classNames('booking__calendar-inputs', {
					'!border-text-700':
						openedInput === INPUT.CALENDAR_FROM ||
						openedInput === INPUT.CALENDAR_TO,
					'calendar-open':
						openedInput === INPUT.CALENDAR_FROM ||
						openedInput === INPUT.CALENDAR_TO,
				})}
			>
				<div
					data-test-id="check-in-input"
					className={classNames(
						'booking__additional-input lg:w-[164px] w-full p-0',
						{
							'lg:w-[170px]': isAr,
							'relative z-20': openedInput === INPUT.CALENDAR_FROM,
						}
					)}
				>
					<FormInput
						id="check-in-date"
						className={classNames(
							' max-lg:h-14 outline-none rounded-s-full',
							{
								capitalize,
								'pointer-events-none': openedInput === INPUT.CALENDAR_FROM,
							}
						)}
						onFocus={() => {
							// Emit date_click only on the closed->open transition, not when
							// moving between the two date fields while the picker is already
							// open (THP-101 B5).
							if (
								openedInput !== INPUT.CALENDAR_FROM &&
								openedInput !== INPUT.CALENDAR_TO
							) {
								emitDateClickGAEvent()
							}
							openCalendar(INPUT.CALENDAR_FROM)
						}}
						value={dateFrom ? format(dateFrom, DATE_FORMAT, { locale }) : ''}
						readonly
						ref={bookingInputDateFromRef}
					>
						<Icons.iconCalendarFrom />
					</FormInput>
				</div>
				<div className="divider-small w-[1px] lg:w-2 bg-text-300 lg:bg-text-400 h-[30px] lg:h-[1px]"></div>
				<div
					className={classNames(
						'booking__additional-input lg:w-[164px] w-full p-0 ms-4 lg:ms-0',
						{
							'lg:w-[170px]': isAr,
							'relative z-20': openedInput === INPUT.CALENDAR_TO,
						}
					)}
					data-test-id="check-out-input"
				>
					<FormInput
						id="check-out-date"
						className={classNames(
							'max-lg:h-14 outline-none rounded-e-full',
							{
								capitalize,
								'pointer-events-none': openedInput === INPUT.CALENDAR_TO,
							}
						)}
						onFocus={() => {
							// Closed->open guard (THP-101 B5); also targets the TO calendar
							// instead of FROM, which previously corrupted the from/to state
							// and highlight (THP-101 B6).
							if (
								openedInput !== INPUT.CALENDAR_FROM &&
								openedInput !== INPUT.CALENDAR_TO
							) {
								emitDateClickGAEvent()
							}
							openCalendar(INPUT.CALENDAR_TO)
						}}
						value={dateTo ? format(dateTo, DATE_FORMAT, { locale }) : ''}
						readonly
						placeholder={t('checkOut')}
					>
						<Icons.iconCalendarTo />
					</FormInput>
				</div>
				{renderDatepicker()}
			</div>
			<div className="max-lg:hidden h-[30px] w-[1px] bg-text-300 mx-1"></div>
		</>
	)

	const renderDatepicker = () => {
		const datepickerContent = (
			<Datepicker
				ref={datepickerRef}
				renderDayContents={renderDayContents}
				onChange={(dates) => {
					try {
						onDateChange(dates as Date[])
					} catch (error) {
						console.error('Error changing date:', error)
						onDateChange([defaultCheckInDate, defaultCheckOutDate])
					}
				}}
				inline
				calendarClassName="new_booking__datepicker"
				startDate={dateFrom}
				endDate={dateTo}
				disabledKeyboardNavigation
				selectsRange
				monthsShown={isMobile ? MOBILE_MONTHS_TO_SHOW : DEFAULT_MONTHS_TO_SHOW}
				minDate={today}
				maxDate={getSearchCalendarMaxDate()}
				openToDate={isMobile ? initialVisibleMonth : undefined}
				locale={locale}
				highlightDates={highlightedDays}
				dayClassName={(date: Date) =>
					classNames({
						'react-datepicker__day--first': isFirstDayOfMonth(date),
						'react-datepicker__day--last': isLastDayOfMonth(date),
					})
				}
				wrapperClassName={classNames({
					'is-opened':
						openedInput === INPUT.CALENDAR_FROM ||
						openedInput === INPUT.CALENDAR_TO,
				})}
			>
				<div className="lg:hidden flex flex-col justify-center items-start w-full">
					<div className="datepicker-header flex justify-start items-center">
						<Icons.iconClose onClick={handleCancelDateSelection} />
						<span className="text-xl font-bold ms-6">{t('dates')}</span>
					</div>
					<div className="datepicker-day-names flex justify-between w-full">
						{getLocalizedDayNames(locale).map((dayName, index) => (
							<div key={index} className="datepicker-day-name ">
								<span>{dayName}</span>
							</div>
						))}
					</div>
				</div>
			</Datepicker>
		)

		if (isMobile) {
			return (
				<RemoveScroll enabled={isDatepickerOpen && isMobile}>
					<FullScreenDrawer
						isOpen={isDatepickerOpen}
						onClose={onCalendarClose}
						headerText={t('dates')}
						overflowHidden
					>
						<div
							className="react-datepicker-popper new-datepicker mobile-datepicker"
							data-test-id="date-picker-popup"
						>
							{datepickerContent}
						</div>
						<div className="datepicker-popup__mobile-inputs">
							<div className="max-w-screen-md mx-auto">
								<div className="px-6 py-3 mb-3 rounded-full border border-text-200">
									<div className="flex justify-between items-center">
										<div className="min-w-32 flex flex-col leading-tight">
											<span className="text-text-800 text-xs">
												{t('checkInDate')}
											</span>
											<span className="font-medium">
												{dateFrom
													? format(dateFrom, DATE_FORMAT, { locale })
													: '-'}
											</span>
										</div>
										<div className="h-[30px] w-[1px] bg-text-200 mx-1"></div>
										<div className="flex-shrink-0 min-w-32 flex flex-col leading-tight">
											<span className="text-text-800 text-xs">
												{t('checkOutDate')}
											</span>
											<span className="font-medium">
												{dateTo ? format(dateTo, DATE_FORMAT, { locale }) : '-'}
											</span>
										</div>
									</div>
								</div>
								<Button
									id="apply-dates-button-mobile"
									bold
									textColor="white"
									onClick={handleApplyDates}
									name="Apply Dates"
									fontSize="xl"
									disabled={!dateFrom || !dateTo}
									className={classNames(
										'h-14 from-main to-[#E02189] !rounded-full transition-all',
										{
											'!bg-text-300': !dateFrom || !dateTo,
											'bg-gradient-to-r': dateFrom && dateTo,
										}
									)}
								>
									{t('applyDates')}
								</Button>
							</div>
						</div>
					</FullScreenDrawer>
				</RemoveScroll>
			)
		} else {
			return (
				<Popup
					className="w-full top-[60px] datepicker-popup"
					isOpen={isDatepickerOpen}
					onClose={onCalendarClose}
				>
					<div
						className="react-datepicker-popper new-datepicker"
						data-test-id="date-picker-popup"
					>
						{datepickerContent}
					</div>
				</Popup>
			)
		}
	}

	return renderCalendarInputs()
}

export default BookingDatepicker
